import React, { Dispatch, useEffect, useState } from 'react';
import {
  CircularProgress,
  Tab as MaterialTab,
  Tabs as MaterialTabs,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tabA11yProps, TabPanel } from 'shared/tabs';
import slugify from 'slugify';
import { SLUGIFY_OPTIONS } from 'shared/constants';
import { indexOfTabMatchingSlug } from 'shared/utilities';

export interface Props {
  currentTabOverride?: number;
  hasButton?: boolean;
  initialTab?: string;
  isLoading?: boolean;
  label: string;
  handleToggleEditModal?: () => void;
  noPanels?: boolean;
  spreadTabs?: boolean;
  setCurrentTabOverride?: Dispatch<number>;
  // Opt-in: called with the slugified tab title when the active tab changes, so
  // a parent on a tab-aware route can push it into the URL (back/forward then
  // restore the tab). Pages that don't pass this keep purely local tab state.
  onTabChange?: (tabSlug: string) => void;
  tabs: Array<Tab>;
}

const StyledTabPanel = styled(TabPanel, {
  shouldForwardProp: (prop) => prop !== 'hasButton',
})<{ hasButton?: boolean }>(({ theme, hasButton }) => ({
  gridColumn: 'start / end',
  gridRow: hasButton ? '5 / 6' : '4 / 5',
  height: 'fit-content',
  position: 'relative',
  '.content__sub &': {
    gridRow: '3 / 4',
    height: 'auto',
    overflowY: 'scroll',
    marginTop: '1px',
  },
  '.content__main &:not([hidden])': {
    minHeight: 'calc(100vh - 23.75rem)',
    alignItems: 'baseline',
  },
  '.content__main &': {
    [theme.breakpoints.up('sm')]: {
      gridRow: '4 / 5',
      '&.report_card': {
        gridTemplateColumns: 'auto',
      },
    },
  },
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    background: 'transparent',
  },
}));

const Tabs: React.FC<Props> = ({
  currentTabOverride,
  hasButton,
  initialTab,
  isLoading,
  label,
  noPanels,
  spreadTabs,
  setCurrentTabOverride,
  onTabChange,
  tabs,
}: Props) => {
  const [currentTab, setCurrentTab] = useState(
    indexOfTabMatchingSlug(tabs, initialTab),
  );
  const prefix = slugify(label, SLUGIFY_OPTIONS);

  // Keep local state in sync when the URL-driven tab changes (e.g. browser
  // back/forward), since useState only seeds on mount.
  useEffect(() => {
    setCurrentTab(indexOfTabMatchingSlug(tabs, initialTab));
  }, [initialTab]);

  // This is kind of hacky but it optionally lets the parent of these tabs
  // know which tab is active.
  const inheritsTabState = typeof currentTabOverride === 'number'
    && setCurrentTabOverride;

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    if (inheritsTabState) {
      setCurrentTabOverride!(newValue);
    } else {
      setCurrentTab(newValue);
    }

    if (onTabChange && tabs[newValue]) {
      onTabChange(slugify(tabs[newValue].title, SLUGIFY_OPTIONS));
    }
  };

  return (
    <>
      <MaterialTabs
        aria-label={label}
        sx={(t) => ({
          gridColumn: 'start / end',
          gridRow: hasButton ? '3 / 4' : '2 / 3',
          padding: 0,
          position: 'relative',
          zIndex: 1,
          '.content__sub &': {
            marginTop: '1rem',
            gridRow: '1 / 2',
          },
          '.content__main &': {
            [t.breakpoints.up('sm')]: {
              gridRow: '2 / 3',
            },
          },
          ...(spreadTabs && {
            '& .MuiTabs-flexContainer': {
              justifyContent: 'space-evenly',
              '& .MuiButtonBase-root': {
                [t.breakpoints.down('md')]: {
                  fontSize: '0.625rem',
                },
              },
            },
          }),
        })}
        onChange={handleTabChange}
        scrollButtons="auto"
        value={inheritsTabState ? currentTabOverride : currentTab}
        variant="scrollable"
      >
        {tabs.map((tab, i) => (
          <MaterialTab
            disableRipple
            key={tab.title}
            label={tab.title}
            icon={tab.icon}
            {...tabA11yProps(i, prefix)}
          />
        ))}
      </MaterialTabs>

      {!noPanels && (isLoading ? (
        <CircularProgress sx={{ margin: '1.25rem' }} />
      ) : (
        tabs.map((tab, i) => (
          <StyledTabPanel
            hasButton={hasButton}
            className={[tab.className, tab.title === 'Reports' ? 'report_card' : ''].filter(Boolean).join(' ')}
            key={tab.title}
            value={inheritsTabState ? currentTabOverride as number : currentTab}
            index={i}
            prefix={prefix}
          >
            {React.createElement(tab.component)}
          </StyledTabPanel>
        ))
      ))}
    </>
  );
};

export default Tabs;
